// frontend/src/components/auth/SessionTimeoutWarning.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  LinearProgress,
  Box
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const SessionTimeoutWarning = () => {
  const { isAuthenticated, logout, checkAuth, user } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes in seconds

  useEffect(() => {
    if (!isAuthenticated || !user) return;

    let warningTimer;
    let countdownInterval;

    // Session timeout - 60 minutes for PM/admin, 30 minutes for reviewers
    const SESSION_TIMEOUT = (user.role === 'super_admin' || user.role === 'admin' || user.role === 'project_manager')
      ? 60 * 60 * 1000  // 60 minutes
      : 30 * 60 * 1000; // 30 minutes
    const WARNING_TIME = 5 * 60 * 1000; // 5 minutes before timeout

    const resetTimers = () => {
      clearTimeout(warningTimer);
      clearInterval(countdownInterval);
      setShowWarning(false);
      setTimeLeft(300);
      startTimers();
    };

    const startTimers = () => {
      // Set warning timer
      warningTimer = setTimeout(() => {
        setShowWarning(true);
        
        // Start countdown
        let remaining = 300;
        countdownInterval = setInterval(() => {
          remaining -= 1;
          setTimeLeft(remaining);
          
          if (remaining <= 0) {
            clearInterval(countdownInterval);
            logout();
          }
        }, 1000);
      }, SESSION_TIMEOUT - WARNING_TIME);
    };

    // Start initial timers
    startTimers();

    // Reset timers on user activity
    const events = ['click', 'keypress', 'scroll', 'mousemove'];
    const handleActivity = () => {
      if (!showWarning) {
        resetTimers();
      }
    };

    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      clearTimeout(warningTimer);
      clearInterval(countdownInterval);
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [isAuthenticated, showWarning, logout, user]);

  const handleContinue = async () => {
    setShowWarning(false);
    setTimeLeft(300);
    // Refresh auth to extend session
    await checkAuth();
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = (timeLeft / 300) * 100;

  return (
    <Dialog
      open={showWarning}
      onClose={() => {}}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon color="warning" />
          <Typography variant="h6">Session Timeout Warning</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography gutterBottom>
          Your session will expire in {formatTime(timeLeft)} due to inactivity.
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Please save your work and click "Continue Session" to remain logged in.
        </Typography>
        <Box mt={2}>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            color={timeLeft < 60 ? "error" : "warning"}
            sx={{ height: 8, borderRadius: 1 }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={logout} color="inherit">
          Logout Now
        </Button>
        <Button onClick={handleContinue} variant="contained" autoFocus>
          Continue Session
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionTimeoutWarning;